import React, { useState } from 'react';
import '../styles/HelpSupport.css';

interface FAQItem {
  question: string;
  answer: string;
  category: 'getting-started' | 'assessment' | 'account' | 'technical' | 'billing';
}

const faqData: FAQItem[] = [
  // Getting Started
  {
    category: 'getting-started',
    question: 'How do I get started with the Scholarship Writing Assistant?',
    answer: 'After signing up, create a student profile for your child by clicking "Add Profile" on the dashboard. Select their year level (3-8) and preferred test format (EDUTEST or ACER). Then choose a writing prompt and let your child complete their writing. Submit it for AI-powered assessment and receive detailed feedback within seconds.'
  },
  {
    category: 'getting-started',
    question: 'What year levels does the platform support?',
    answer: 'The platform supports Australian students from Year 3 to Year 8. Each year level has age-appropriate writing prompts and assessment criteria tailored to the expected skill level.'
  },
  {
    category: 'getting-started',
    question: 'What is the difference between EDUTEST and ACER formats?',
    answer: 'EDUTEST and ACER are two major scholarship testing organisations in Australia. While both assess similar writing skills, they have slightly different rubrics and expectations. Our platform provides prompts and assessments aligned with each format, so your child can practice specifically for their target test.'
  },
  {
    category: 'getting-started',
    question: 'Can I create multiple student profiles?',
    answer: 'Yes! You can create multiple student profiles within a single account, perfect for families with multiple children. Each profile tracks progress independently, allowing you to monitor each child\'s improvement over time.'
  },
  // Assessment
  {
    category: 'assessment',
    question: 'How does the AI assessment work?',
    answer: 'Our AI assessment uses advanced language models trained on official EDUTEST and ACER rubrics. It evaluates writing across six criteria: Punctuation, Creativity, Text Construction, Grammar, Spelling, and Relevance to Task. Each criterion is scored from 0-10, with detailed feedback explaining the score.'
  },
  {
    category: 'assessment',
    question: 'How accurate is the AI scoring compared to real tests?',
    answer: 'Our AI is calibrated to match official scholarship test standards. While no automated system is perfect, our assessments provide reliable feedback that closely aligns with what markers look for in actual EDUTEST and ACER tests. Use the scores as a guide for improvement rather than exact predictions.'
  },
  {
    category: 'assessment',
    question: 'What do the scores mean?',
    answer: 'Scores range from 0-10 for each criterion. Generally: 9-10 is Excellent, 7-8 is Good, 5-6 is Satisfactory, 3-4 is Developing, and 0-2 Needs Improvement. The detailed feedback explains specifically what your child did well and what they can improve.'
  },
  {
    category: 'assessment',
    question: 'How long does assessment take?',
    answer: 'Most assessments complete within 10-30 seconds. You\'ll see a progress indicator while the AI analyses the writing. If it takes longer, please wait - complex writings may require more processing time.'
  },
  {
    category: 'assessment',
    question: 'Can I see example writings to understand what good writing looks like?',
    answer: 'Yes! Each assessment report includes an "Example Writing" tab where you can view sample writings at different quality levels (Excellent, Good, Developing) for the same prompt. This helps your child understand what markers are looking for.'
  },
  // Account
  {
    category: 'account',
    question: 'How do I update my child\'s year level?',
    answer: 'Go to the Dashboard and click on the student profile you want to update. Click "Edit Profile" and change the year level. Note that changing the year level will affect which prompts are shown and how assessments are calibrated.'
  },
  {
    category: 'account',
    question: 'How do I delete a student profile?',
    answer: 'On the Dashboard, click on the student profile and select "Delete Profile". This will permanently remove the profile and all associated assessment history. This action cannot be undone.'
  },
  {
    category: 'account',
    question: 'How do I change my password?',
    answer: 'Click on your account settings (top right corner) and select "Change Password". You\'ll need to enter your current password and then your new password twice to confirm.'
  },
  {
    category: 'account',
    question: 'Can I access my account from multiple devices?',
    answer: 'Yes! Your account can be accessed from any device with a web browser. All your data, including student profiles and assessment history, is synced across devices.'
  },
  // Technical
  {
    category: 'technical',
    question: 'What browsers are supported?',
    answer: 'The platform works best on modern browsers including Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated to the latest version for the best experience.'
  },
  {
    category: 'technical',
    question: 'My writing didn\'t save - what happened?',
    answer: 'If your submission failed, the platform automatically preserves your work. Try refreshing the page and resubmitting. If the problem persists, check your internet connection. Your writing should still be in the text area when you return.'
  },
  {
    category: 'technical',
    question: 'The assessment is taking too long - what should I do?',
    answer: 'Assessments typically complete within 30 seconds. If it\'s taking longer: 1) Wait a bit more as complex writings take longer, 2) Check your internet connection, 3) If it fails, you can resubmit the writing. Your work is preserved.'
  },
  {
    category: 'technical',
    question: 'I\'m getting an error message - what should I do?',
    answer: 'First, try refreshing the page. If the error persists, clear your browser cache and try again. If you continue to experience issues, please contact our support team with details about the error message and what you were trying to do.'
  },
  // Billing
  {
    category: 'billing',
    question: 'How does the pay-as-you-go pricing work?',
    answer: 'We offer simple, transparent pricing with no subscriptions. You purchase writing credits that never expire. Choose from: 1 Free Writing to try the service, 5 Writings for $20 ($4 each), 15 Writings for $50 ($3.33 each), or 1 Writing for $5. Use your credits whenever you need feedback.'
  },
  {
    category: 'billing',
    question: 'Do my writing credits expire?',
    answer: 'No! Your writing credits never expire. Use them at your own pace, whether that\'s all in one week or spread across several months. There\'s no pressure to use them by a certain date.'
  },
  {
    category: 'billing',
    question: 'Is there a subscription or automatic renewal?',
    answer: 'No subscriptions and no automatic renewals. You only pay for what you need, when you need it. Once you use your credits, you can purchase more whenever you\'re ready.'
  },
  {
    category: 'billing',
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor. All transactions are encrypted and secure.'
  },
  {
    category: 'billing',
    question: 'Can I get a refund?',
    answer: 'Since credits never expire, we generally don\'t offer refunds. However, if you experience technical issues that prevent you from using the service, please contact our support team and we\'ll work with you to resolve the situation.'
  }
];

interface UserGuide {
  title: string;
  icon: string;
  steps: string[];
}

const userGuides: UserGuide[] = [
  {
    title: 'Getting Started',
    icon: '🚀',
    steps: [
      'Sign up for an account using your email address',
      'Verify your email by clicking the link we send you',
      'Log in and create a student profile for your child',
      'Select your child\'s year level (3-8) and test format (EDUTEST/ACER)',
      'Choose a writing prompt and start practising!'
    ]
  },
  {
    title: 'Completing a Writing Assessment',
    icon: '✍️',
    steps: [
      'Select a student profile from your dashboard',
      'Choose a writing prompt category (Creative, Persuasive, or Narrative)',
      'Select a specific prompt from the available options',
      'Have your child write their response in the text area',
      'Click "Submit for Assessment" when finished',
      'Wait for the AI to analyse the writing (usually 10-30 seconds)',
      'Review the detailed feedback and scores'
    ]
  },
  {
    title: 'Understanding Your Results',
    icon: '📊',
    steps: [
      'View the Overview tab for a summary of scores and key strengths/weaknesses',
      'Check the Detailed Feedback tab for criterion-by-criterion analysis',
      'Review the Improvement Plan tab for specific tips on how to improve',
      'Compare with Example Writings to see what good writing looks like',
      'Track progress over time in the Progress Dashboard'
    ]
  },
  {
    title: 'Tracking Progress',
    icon: '📈',
    steps: [
      'Go to the Progress Dashboard from the main menu',
      'Select a student profile to view their history',
      'See average scores across all criteria',
      'View score trends over time',
      'Click on any past assessment to review detailed feedback'
    ]
  },
  {
    title: 'Using Example Writings',
    icon: '📚',
    steps: [
      'After completing an assessment, click the "Example Writing" tab',
      'Select a quality level (Excellent, Good, or Developing)',
      'Read the example writing for the same prompt',
      'Note the techniques and elements highlighted',
      'Compare with your child\'s writing to identify areas for improvement'
    ]
  }
];

type CategoryFilter = 'all' | FAQItem['category'];

export const HelpSupport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'faq' | 'guides' | 'contact'>('faq');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = categoryFilter === 'all' || faq.category === categoryFilter;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryLabel = (category: FAQItem['category']): string => {
    const labels: Record<FAQItem['category'], string> = {
      'getting-started': 'Getting Started',
      'assessment': 'Assessment',
      'account': 'Account',
      'technical': 'Technical',
      'billing': 'Billing'
    };
    return labels[category];
  };

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="help-support">
      <div className="help-header">
        <h1 className="help-title">Help & Support</h1>
        <p className="help-subtitle">
          Find answers to common questions and learn how to get the most out of the Australian Scholarship Writing Assistant
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="help-tabs">
        <button
          className={`help-tab ${activeTab === 'faq' ? 'help-tab--active' : ''}`}
          onClick={() => setActiveTab('faq')}
        >
          <span className="tab-icon">❓</span>
          FAQs
        </button>
        <button
          className={`help-tab ${activeTab === 'guides' ? 'help-tab--active' : ''}`}
          onClick={() => setActiveTab('guides')}
        >
          <span className="tab-icon">📖</span>
          User Guides
        </button>
        <button
          className={`help-tab ${activeTab === 'contact' ? 'help-tab--active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          <span className="tab-icon">✉️</span>
          Contact Support
        </button>
      </div>

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <div className="help-content help-content--faq">
          {/* Search and Filter */}
          <div className="faq-controls">
            <div className="faq-search">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="faq-filters">
              <button
                className={`filter-btn ${categoryFilter === 'all' ? 'filter-btn--active' : ''}`}
                onClick={() => setCategoryFilter('all')}
              >
                All
              </button>
              {(['getting-started', 'assessment', 'account', 'technical', 'billing'] as const).map(cat => (
                <button
                  key={cat}
                  className={`filter-btn ${categoryFilter === cat ? 'filter-btn--active' : ''}`}
                  onClick={() => setCategoryFilter(cat)}
                >
                  {getCategoryLabel(cat)}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ List */}
          <div className="faq-list">
            {filteredFAQs.length === 0 ? (
              <div className="no-results">
                <span className="no-results-icon">🔍</span>
                <p>No FAQs found matching your search.</p>
                <button 
                  className="clear-search-btn"
                  onClick={() => { setSearchQuery(''); setCategoryFilter('all'); }}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              filteredFAQs.map((faq, index) => (
                <div 
                  key={index} 
                  className={`faq-item ${expandedFAQ === index ? 'faq-item--expanded' : ''}`}
                >
                  <button 
                    className="faq-question"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expandedFAQ === index}
                  >
                    <span className="faq-category-badge">{getCategoryLabel(faq.category)}</span>
                    <span className="faq-question-text">{faq.question}</span>
                    <span className="faq-toggle">{expandedFAQ === index ? '−' : '+'}</span>
                  </button>
                  {expandedFAQ === index && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* User Guides Tab */}
      {activeTab === 'guides' && (
        <div className="help-content help-content--guides">
          <div className="guides-grid">
            {userGuides.map((guide, index) => (
              <div key={index} className="guide-card">
                <div className="guide-header">
                  <span className="guide-icon">{guide.icon}</span>
                  <h3 className="guide-title">{guide.title}</h3>
                </div>
                <ol className="guide-steps">
                  {guide.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="guide-step">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact Support Tab */}
      {activeTab === 'contact' && (
        <div className="help-content help-content--contact">
          <div className="contact-options">
            <div className="contact-card">
              <span className="contact-icon">📧</span>
              <h3 className="contact-title">Email Support</h3>
              <p className="contact-description">
                Send us an email and we'll get back to you within 24-48 hours.
              </p>
              <a href="mailto:support@scholarwrite.com.au" className="contact-link">
                support@scholarwrite.com.au
              </a>
            </div>

            <div className="contact-card">
              <span className="contact-icon">📚</span>
              <h3 className="contact-title">Knowledge Base</h3>
              <p className="contact-description">
                Browse our comprehensive guides and tutorials.
              </p>
              <button 
                className="contact-btn"
                onClick={() => setActiveTab('guides')}
              >
                View Guides
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Help Footer removed */}
    </div>
  );
};

export default HelpSupport;
